/* Factumis App — Création de facture (/factures/nouvelle).
   Assistant en 4 étapes : (1) Destinataire (recherche SIREN → préremplissage),
   (2) Lignes de facturation (description, quantité, prix unitaire, TVA),
   (3) Dates et conditions (émission, échéance, mode de paiement),
   (4) Aperçu et envoi via POST /api/b2b/invoices.
   Soft-limit : le dépassement du quota du plan n'empêche pas l'envoi (bandeau « over » +
   invite à l'upgrade). Seul le plafond anti-abus serveur renvoie un 402, géré à l'envoi. */
import { useState, useEffect, useMemo, useRef } from 'react';
import { Icon } from '../lib/icons';
import { Button, ConfirmDialog, Field, Timeline, Illus, useCombobox, eur } from '../components/ui';
import { LineBuilder, linesToPayload, type Line } from '../components/LineBuilder';
import { TemplatePicker } from '../components/TemplatePicker';
import { useCatalog, useClients, getClientDetail, type InvoiceTemplate, type ClientDetail } from '../data/live';
import { useGo } from '../shell/nav';
import { apiGet, apiPost, apiDelete, ApiError, errorMessage } from '../lib/api';
import { useMe } from '../lib/useMe';
import { emittedQuotaLevel } from '../lib/quota';
import type { Invoice } from '../data/demo';

// Contrat renvoyé par GET /api/companies/search (proxy annuaire Sirene public).
interface Company {
  siren: string;
  name: string;
  address: string | null;
  postalCode: string | null;
  city: string | null;
  vatNumber: string | null;
  active: boolean;
  /** false = diffusion protégée INSEE : nom/adresse blanchis par le serveur. */
  diffusible: boolean;
}

const STEPS = ['À qui ?', 'Quoi ?', 'Quand & comment ?', 'Aperçu'];

function StepBar({ step }: { step: number }) {
  const cur = step;
  const out: JSX.Element[] = [];
  STEPS.forEach((s, i) => {
    const n = i + 1;
    const state = n < cur ? 'done' : n === cur ? 'active' : '';
    out.push(
      <div key={'st' + i} className={'wiz__step ' + state}>
        <span className="wiz__num">{n < cur ? <Icon name="check" size={13} /> : n}</span>
        <span className="wiz__lbl">{s}</span>
      </div>,
    );
    if (i < STEPS.length - 1) out.push(<span key={'sp' + i} className={'wiz__sep' + (n < cur ? ' done' : '')} />);
  });
  return <div className="wiz__steps">{out}</div>;
}

function Preview({ client, lines, issuer }: { client: string; lines: Line[]; issuer: { name: string; siren: string | null } }) {
  const ht = lines.reduce((s, l) => s + l.q * l.pu, 0);
  const tva = lines.reduce((s, l) => s + l.q * l.pu * (l.tva / 100), 0);
  return (
    <div className="preview">
      <div className="card card--pad">
        <div className="preview__tag">
          <Icon name="check-c" size={14} />
          Aperçu Factur-X
        </div>
        <div className="preview__doc">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 16, letterSpacing: '-.03em' }}>{issuer.name}</div>
              {issuer.siren ? <div style={{ fontSize: 10.5, color: '#59616E' }}>SIREN {issuer.siren}</div> : null}
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>FACTURE</div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: '#59616E' }}>Brouillon</div>
            </div>
          </div>
          <div style={{ background: '#F2F3F1', borderRadius: 8, padding: '9px 11px', fontSize: 10.5, marginBottom: 14 }}>
            <div style={{ color: '#59616E' }}>Facturé à</div>
            <div style={{ fontWeight: 600 }}>{client || 'Sélectionnez un client'}</div>
          </div>
          {lines
            .filter((l) => l.d)
            .map((l, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, padding: '5px 0', borderBottom: '1px solid #EEEFEC' }}>
                <span>{l.d}</span>
                <span style={{ fontFamily: 'var(--font-mono)' }}>{eur(l.q * l.pu)}</span>
              </div>
            ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12, fontSize: 13, fontWeight: 700 }}>
            <span>Total TTC</span>
            <span style={{ fontFamily: 'var(--font-mono)' }}>{eur(ht + tva)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function CreateInvoice() {
  const go = useGo();
  const { me } = useMe();
  const eq = emittedQuotaLevel(me);
  // Émetteur = l'organisation réelle de l'utilisateur (plus de société factice).
  const issuer = { name: me?.org?.name ?? 'Votre entreprise', siren: me?.org?.siren ?? null };
  const today = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
  const [step, setStep] = useState(1);
  const [client, setClient] = useState('');
  const [email, setEmail] = useState('');
  // Recherche d'entreprise (annuaire Sirene via le backend).
  const [selected, setSelected] = useState<Company | null>(null);
  const [results, setResults] = useState<Company[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchErr, setSearchErr] = useState<string | null>(null);
  const [dropOpen, setDropOpen] = useState(false);
  // Fiche locale sélectionnée (« Vos clients »), chargée en détail (pays, TVA
  // intracom) car la liste GET /api/b2b/clients ne porte pas countryCode.
  const clients = useClients();
  const [selectedContact, setSelectedContact] = useState<ClientDetail | null>(null);
  const [contactLoading, setContactLoading] = useState(false);
  // Fiche locale dont le chargement du pays a échoué : la sélection reste NON
  // résolue (jamais de présomption FR ne pouvant partir en TVA non verrouillée),
  // conservée seulement pour proposer un raccourci « Réessayer ».
  const [failedContactPick, setFailedContactPick] = useState<{ id: number; name: string } | null>(null);
  const contactFetchSeqRef = useRef(0);
  const isForeignClient = selectedContact ? selectedContact.countryCode !== 'FR' : false;
  const [lines, setLines] = useState<Line[]>([{ d: '', q: 1, pu: 0, tva: 20 }]);
  const catalog = useCatalog();
  const [dueDays, setDueDays] = useState(30);
  const [confirm, setConfirm] = useState(false);
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdNumber, setCreatedNumber] = useState<string>('');
  const [sentReal, setSentReal] = useState(false); // true = transmise, false = brouillon créé
  const [sendNote, setSendNote] = useState<string | null>(null);
  // Émission en 3 temps (draft → validate-data → send) : le brouillon est créé dès
  // l'ouverture de la confirmation (avant tout envoi réel), pour pouvoir prévalider
  // ses données et n'activer l'émission qu'une fois le brouillon exploitable.
  const [draftInvoiceId, setDraftInvoiceId] = useState<string | null>(null);
  // Payload sérialisé qui a servi à créer le brouillon courant : si l'utilisateur
  // revient en arrière et modifie la facture, la comparaison détecte l'écart et
  // force la re-création du brouillon (sinon le send émettrait les ANCIENNES lignes).
  const draftPayloadRef = useRef<string | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [validation, setValidation] = useState<{ errors: string[]; warnings: string[] } | null>(null);

  const ht = lines.reduce((s, l) => s + l.q * l.pu, 0);
  const tva = lines.reduce((s, l) => s + l.q * l.pu * (l.tva / 100), 0);

  function applyTemplate(t: InvoiceTemplate) {
    setLines(t.lines.map((l) => ({ d: l.label, q: l.quantity, pu: l.unitPriceCents / 100, tva: l.vatRate })));
    if (t.defaultDueDays != null) setDueDays(t.defaultDueDays);
  }

  // Fiches locales correspondant à la saisie (« Vos clients »), filtrées côté
  // client sur une liste déjà chargée une fois : pas de débounce nécessaire.
  // Mémoïsé : useCombobox réinitialise la surbrillance clavier (setActive(-1)) à
  // chaque fois que la référence d'`items` change (useEffect([open, items])) ; un
  // tableau recréé à chaque rendu cassait donc la navigation aux flèches (constat
  // de revue Critical).
  const trimmedClient = client.trim();
  const localMatches = useMemo(
    () =>
      trimmedClient.length > 0
        ? clients.rows.filter((c) => c.id != null && c.name.toLowerCase().includes(trimmedClient.toLowerCase())).slice(0, 6)
        : [],
    [trimmedClient, clients.rows],
  );

  // Recherche débounce contre l'annuaire Sirene (proxy backend). Ne relance pas
  // tant que la saisie correspond à l'entreprise ou à la fiche locale déjà
  // sélectionnée. Les fiches locales ouvrent la liste sans attendre le débounce
  // (déjà en mémoire), y compris en dessous du seuil de 3 caractères Sirene.
  useEffect(() => {
    if (selected && client === selected.name) return;
    if (selectedContact && client === selectedContact.name) return;
    const q = client.trim();
    const hasLocal = q.length > 0 && clients.rows.some((c) => c.id != null && c.name.toLowerCase().includes(q.toLowerCase()));
    if (q.length < 3) {
      setResults([]);
      setDropOpen(hasLocal);
      return;
    }
    const ctrl = new AbortController();
    const t = setTimeout(() => {
      setSearching(true);
      setSearchErr(null);
      apiGet<{ results: Company[] }>(`/api/companies/search?q=${encodeURIComponent(q)}`, ctrl.signal)
        .then((d) => {
          setResults(d.results);
          setDropOpen(true);
        })
        .catch((err) => {
          if (!ctrl.signal.aborted) {
            setResults([]);
            setSearchErr(errorMessage(err, "Recherche d'entreprise indisponible."));
            setDropOpen(hasLocal);
          }
        })
        .finally(() => {
          if (!ctrl.signal.aborted) setSearching(false);
        });
    }, 300);
    return () => {
      ctrl.abort();
      clearTimeout(t);
    };
  }, [client, selected, selectedContact, clients.rows]);

  function pickCompany(c: Company) {
    setSelected(c);
    setSelectedContact(null);
    setFailedContactPick(null);
    setClient(c.name);
    setResults([]);
    setDropOpen(false);
    setSearchErr(null);
  }

  // Sélection d'une fiche locale : la liste (GET /api/b2b/clients) ne porte pas
  // le pays, on charge donc la fiche complète pour savoir si le client est
  // étranger (TVA forcée à 0). `contactFetchSeqRef` écarte une réponse périmée
  // si l'utilisateur enchaîne les sélections avant la fin du chargement.
  async function pickLocalClient(c: { id?: number; name: string }) {
    if (c.id == null) return;
    const target = { id: c.id, name: c.name };
    setSelected(null);
    setClient(target.name);
    setResults([]);
    setDropOpen(false);
    setSearchErr(null);
    setSelectedContact(null);
    setFailedContactPick(null);
    setContactLoading(true);
    const seq = ++contactFetchSeqRef.current;
    try {
      const detail = await getClientDetail(target.id);
      if (seq !== contactFetchSeqRef.current) return;
      setSelectedContact(detail);
    } catch (err) {
      if (seq !== contactFetchSeqRef.current) return;
      // Le pays n'a pas pu être déterminé : on ne garde ni le nom saisi ni une
      // fiche à moitié sélectionnée (ça partirait sans TVA verrouillée si le
      // client est en réalité étranger). L'utilisateur re-choisit, ou relance
      // le même chargement via le bouton Réessayer.
      setClient('');
      setSelectedContact(null);
      setFailedContactPick(target);
      setSearchErr(errorMessage(err, 'Chargement de la fiche client impossible.'));
    } finally {
      if (seq === contactFetchSeqRef.current) setContactLoading(false);
    }
  }

  type ComboItem = { kind: 'local'; client: { id?: number; name: string; siren: string; city: string } } | { kind: 'sirene'; company: Company };
  // Mémoïsé pour la même raison que `localMatches` : une référence stable tant que
  // son contenu ne change pas réellement, sinon la navigation clavier casse.
  const comboItems: ComboItem[] = useMemo(
    () => [
      ...localMatches.map((c) => ({ kind: 'local' as const, client: c })),
      ...results.map((c) => ({ kind: 'sirene' as const, company: c })),
    ],
    [localMatches, results],
  );

  const combo = useCombobox({
    open: dropOpen,
    items: comboItems,
    onSelect: (item) => (item.kind === 'local' ? void pickLocalClient(item.client) : pickCompany(item.company)),
    onClose: () => setDropOpen(false),
  });

  // Payload commun création/prévalidation. Une fiche locale fournit contactId
  // (résolution prioritaire côté serveur) ; sinon une entreprise Sirene sélectionnée
  // fournit le SIREN ; sinon une saisie brute ≥9 chiffres est envoyée comme SIREN
  // pour que le backend construise la fiche, un simple nom étant apparié sur les contacts.
  function buildPayload() {
    const typedDigits = client.replace(/\D/g, '');
    const siren = selected?.siren ?? (typedDigits.length >= 9 ? typedDigits : undefined);
    return {
      client: (selected?.name ?? client).trim(),
      siren,
      contactId: selectedContact?.id,
      email: email.trim() || undefined,
      send: false,
      lines: linesToPayload(lines),
      dueDays,
    };
  }

  // Client étranger : TVA non applicable, les lignes déjà saisies (ex. avant la
  // sélection du client) sont ramenées à 0 plutôt que de laisser une facture
  // incohérente. Le taux d'avant verrouillage voyage AVEC chaque ligne
  // (`preLockTva`, cf. LineBuilder.tsx) plutôt que dans un tableau parallèle par
  // position : une restauration par index se désalignait dès qu'une ligne était
  // ajoutée ou supprimée pendant le verrou (taux croisés au retour vers un client
  // français). Une ligne ajoutée pendant le verrou (donc sans `preLockTva`)
  // retombe sur le défaut 20 % au déverrouillage.
  const wasForeignRef = useRef(false);
  useEffect(() => {
    if (isForeignClient && !wasForeignRef.current) {
      setLines((prev) => prev.map((l) => ({ ...l, preLockTva: l.tva, tva: 0 })));
    } else if (!isForeignClient && wasForeignRef.current) {
      setLines((prev) => prev.map((l) => ({ ...l, tva: l.preLockTva ?? 20, preLockTva: undefined })));
    }
    wasForeignRef.current = isForeignClient;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isForeignClient]);

  // Le brouillon prêt à être émis décrit-il bien la facture affichée à l'écran ?
  // Recalculé à chaque rendu : c'est la condition qui autorise l'émission.
  const draftMatchesScreen = Boolean(draftInvoiceId) && draftPayloadRef.current === JSON.stringify(buildPayload());

  // Prépare le brouillon pour UN payload donné : création puis prévalidation
  // Seqino (validate-data), affichées avant d'autoriser l'émission réelle.
  //
  // Concurrence. Depuis que l'annulation est possible pendant la préparation,
  // deux préparations peuvent se chevaucher : l'utilisateur annule, modifie la
  // facture, rouvre. Sans garde, la préparation périmée se résolvait APRÈS et
  // posait draftInvoiceId / draftPayloadRef sur l'ANCIEN payload, si bien que
  // l'émission partait sur l'ancien brouillon sous le récapitulatif du nouveau.
  // `prepareSeqRef` numérote les préparations : seule la dernière lancée a le
  // droit d'écrire l'état. Une préparation périmée jette son propre brouillon
  // (ni numéro ni jeton consommés à ce stade) et ne touche à rien.
  const prepareSeqRef = useRef(0);
  const preparingKeyRef = useRef<string | null>(null); // payload de la préparation en vol

  async function prepareDraft(payloadKey: string) {
    const seq = ++prepareSeqRef.current;
    const staleDraftId = draftInvoiceId; // brouillon remplacé, à nettoyer côté Seqino
    preparingKeyRef.current = payloadKey;
    setPreparing(true);
    setError(null);
    setValidation(null);
    setDraftInvoiceId(null);
    draftPayloadRef.current = null;
    try {
      const res = await apiPost<{ invoice: Invoice }>('/api/b2b/invoices', buildPayload());
      const id = res.invoice.invoiceId;
      if (seq !== prepareSeqRef.current) {
        // Préparation périmée : son brouillon ne doit jamais devenir l'émettable.
        if (id) void apiDelete(`/api/b2b/invoices/${id}`).catch(() => {});
        return;
      }
      // Nettoyage best-effort de l'ancien brouillon : ni jeton ni numéro en jeu,
      // un échec silencieux laisse simplement un orphelin supprimable en liste.
      if (staleDraftId) void apiDelete(`/api/b2b/invoices/${staleDraftId}`).catch(() => {});
      setCreatedNumber(res.invoice.id);
      if (!id) return;
      setDraftInvoiceId(id);
      draftPayloadRef.current = payloadKey;
      try {
        const v = await apiPost<{ valid: boolean; errors: string[]; warnings: string[] }>(`/api/b2b/invoices/${id}/validate-data`, {});
        if (seq === prepareSeqRef.current) setValidation({ errors: v.errors, warnings: v.warnings });
      } catch {
        // Prévalidation indisponible (best-effort) : ne bloque pas l'émission pour autant.
        if (seq === prepareSeqRef.current) setValidation(null);
      }
    } catch (err) {
      if (seq !== prepareSeqRef.current) return; // l'échec d'une préparation périmée n'intéresse plus personne
      if (err instanceof ApiError && err.status === 402) {
        setError("Plafond mensuel d'émission atteint pour votre formule. Passez à une formule supérieure pour continuer.");
      } else {
        setError(errorMessage(err, 'Création du brouillon impossible. Réessayez.'));
      }
    } finally {
      // Seule la préparation courante rend la main : sinon une périmée qui se
      // termine rouvrirait le bouton d'émission d'une préparation encore en vol.
      if (seq === prepareSeqRef.current) {
        setPreparing(false);
        preparingKeyRef.current = null;
      }
    }
  }

  // Ouvre la confirmation, et relance une préparation dès que le brouillon
  // connu ne correspond plus à l'écran. Trois cas, aucun ne laisse l'utilisateur
  // devant un bouton désactivé sans issue : brouillon à jour (rien à faire),
  // préparation déjà en vol POUR CE payload (on attend), sinon on (re)prépare,
  // y compris quand une préparation périmée est encore en vol.
  function openConfirm() {
    setConfirm(true);
    const payloadKey = JSON.stringify(buildPayload());
    if (draftInvoiceId && payloadKey === draftPayloadRef.current) return;
    if (preparingKeyRef.current === payloadKey) return;
    void prepareDraft(payloadKey);
  }

  // Émet le brouillon déjà créé (draftInvoiceId). En beta, l'envoi réel peut être
  // désactivé (sandbox) : le brouillon existe et reste prêt à être transmis, on
  // affiche alors l'écran de succès en mode « brouillon créé » plutôt qu'une erreur.
  async function submit() {
    if (!draftInvoiceId) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await apiPost<{ invoice: Invoice; sent: boolean }>(`/api/b2b/invoices/${draftInvoiceId}/send`, {});
      // L'émission assigne le numéro légal séquentiel : c'est LUI que l'écran de
      // succès affiche (l'id opaque du brouillon n'est qu'un identifiant technique).
      setCreatedNumber(res.invoice.id);
      setSentReal(true);
      setSendNote(null);
      setConfirm(false);
      setSent(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 402) {
        setError("Plafond mensuel d'émission atteint pour votre formule. Passez à une formule supérieure pour continuer.");
      } else if (err instanceof ApiError && err.code === 'SEND_DISABLED') {
        setSentReal(false);
        setSendNote(err.message);
        setConfirm(false);
        setSent(true);
      } else {
        setError(errorMessage(err, "L’envoi n’a pas abouti. Vérifiez l’état de la facture dans Factures avant de réessayer."));
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (sent) {
    return (
      <div className="empty" style={{ paddingTop: 40 }}>
        <Illus glyph="check-c" id="sent" />
        <h3>{sentReal ? 'Votre facture est partie !' : 'Votre facture est créée !'}</h3>
        <p>
          {(createdNumber || 'La facture') +
            (sentReal
              ? ' a été transmise à ' + (client || 'votre client') + ' au format Factur-X. Vous serez prévenu à chaque étape.'
              : ' a été enregistrée pour ' + (client || 'votre client') + ' au format Factur-X.')}
        </p>
        {!sentReal && sendNote ? (
          <div className="callout-info" style={{ maxWidth: 420, margin: '0 auto 18px', textAlign: 'left' }}>
            <Icon name="info" size={18} />
            <p>{sendNote + ' La facture reste en brouillon, prête à être envoyée.'}</p>
          </div>
        ) : null}
        <div className="card card--pad" style={{ maxWidth: 360, margin: '8px auto 22px', textAlign: 'left' }}>
          <Timeline
            steps={[
              { label: 'Créée', when: 'À l’instant', state: 'done' },
              { label: 'Envoyée', when: 'À l’instant', state: 'current' },
              { label: 'Reçue par le client', state: 'future' },
              { label: 'Payée', state: 'future' },
            ]}
          />
        </div>
        <div className="empty .actions" style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <Button variant="primary" onClick={() => go('invoices')}>
            Voir mes factures
          </Button>
          <Button
            variant="ghost"
            icon="plus"
            onClick={() => {
              setSent(false);
              setStep(1);
            }}
          >
            Nouvelle facture
          </Button>
        </div>
      </div>
    );
  }

  const panels: Record<number, JSX.Element> = {
    1: (
      <div className="wiz__panel">
        <div className="wiz__qhead">À qui adressez-vous cette facture ?</div>
        <div className="wiz__qsub">Recherchez un client existant ou créez-le à partir de son SIREN.</div>
        <Field
          label="Client"
          help={selected || selectedContact ? null : 'Saisissez un nom ou un numéro SIREN, puis choisissez dans la liste.'}
          ok={
            selected
              ? 'Entreprise sélectionnée : informations récupérées automatiquement.'
              : selectedContact
                ? 'Fiche client sélectionnée.'
                : null
          }
          error={searchErr ?? undefined}
        >
          <div className="input-wrap" style={{ position: 'relative' }}>
            <input
              className="ctrl"
              placeholder="Nom du client ou SIREN"
              value={client}
              autoComplete="off"
              {...combo.inputProps}
              onChange={(ev) => {
                setClient(ev.target.value);
                if (selected) setSelected(null);
                if (selectedContact) setSelectedContact(null);
                if (failedContactPick) setFailedContactPick(null);
              }}
              onFocus={() => {
                if (results.length || localMatches.length) setDropOpen(true);
              }}
              onBlur={() => setTimeout(() => setDropOpen(false), 120)}
            />
            <span
              className="ic-r"
              style={{ color: selected || selectedContact ? 'var(--laiton-text)' : 'var(--text-muted, #59616E)', opacity: searching || contactLoading ? 0.5 : 1 }}
            >
              {selected || selectedContact ? <Icon name="check-c" size={18} /> : <Icon name="search" size={16} />}
            </span>
            {dropOpen && (localMatches.length > 0 || results.length > 0) ? (
              <ul
                {...combo.listProps}
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 4px)',
                  left: 0,
                  right: 0,
                  zIndex: 20,
                  margin: 0,
                  padding: 4,
                  listStyle: 'none',
                  background: 'var(--surface, #fff)',
                  border: '1px solid var(--border-subtle, #E3E5E1)',
                  borderRadius: 'var(--r-md, 10px)',
                  boxShadow: '0 8px 28px rgba(0,0,0,.12)',
                  maxHeight: 280,
                  overflowY: 'auto',
                }}
              >
                {localMatches.length > 0 ? (
                  <>
                    <li
                      aria-hidden="true"
                      style={{ padding: '6px 10px 2px', fontSize: 11, fontWeight: 700, color: 'var(--text-muted, #59616E)', textTransform: 'uppercase', letterSpacing: '.02em' }}
                    >
                      Vos clients
                    </li>
                    {localMatches.map((c, i) => (
                      <li
                        key={'local-' + c.id}
                        {...combo.optionProps(i)}
                        onMouseDown={() => void pickLocalClient(c)}
                        style={{
                          padding: '9px 10px',
                          borderRadius: 8,
                          cursor: 'pointer',
                          background: combo.active === i ? 'var(--surface-sunken, #F2F3F1)' : 'transparent',
                        }}
                      >
                        <div style={{ fontWeight: 600, fontSize: 14 }}>{c.name || 'Nom non renseigné'}</div>
                        <div className="muted" style={{ fontSize: 12 }}>
                          {c.siren ? 'SIREN ' + c.siren : 'Client étranger'}
                          {c.city && c.city !== '—' ? ' · ' + c.city : ''}
                        </div>
                      </li>
                    ))}
                  </>
                ) : null}
                {results.length > 0 ? (
                  <>
                    <li
                      aria-hidden="true"
                      style={{ padding: '6px 10px 2px', fontSize: 11, fontWeight: 700, color: 'var(--text-muted, #59616E)', textTransform: 'uppercase', letterSpacing: '.02em' }}
                    >
                      Annuaire Sirene
                    </li>
                    {results.map((c, i) => {
                      const idx = localMatches.length + i;
                      return (
                        <li
                          key={c.siren}
                          {...combo.optionProps(idx)}
                          onMouseDown={() => pickCompany(c)}
                          style={{
                            padding: '9px 10px',
                            borderRadius: 8,
                            cursor: 'pointer',
                            background: combo.active === idx ? 'var(--surface-sunken, #F2F3F1)' : 'transparent',
                          }}
                        >
                          <div style={{ fontWeight: 600, fontSize: 14 }}>{c.diffusible === false ? 'Entreprise non diffusible' : c.name || 'Nom non renseigné'}</div>
                          <div className="muted" style={{ fontSize: 12 }}>
                            SIREN {c.siren}
                            {c.city ? ' · ' + c.city : ''}
                            {!c.active ? ' · cessée' : ''}
                          </div>
                        </li>
                      );
                    })}
                  </>
                ) : null}
              </ul>
            ) : null}
          </div>
        </Field>
        {failedContactPick ? (
          <div>
            <Button variant="ghost" size="sm" onClick={() => void pickLocalClient(failedContactPick)}>
              Réessayer
            </Button>
          </div>
        ) : null}
        {selectedContact ? (
          <div className="card--pad" style={{ background: 'var(--surface-sunken)', borderRadius: 'var(--r-md)' }}>
            <div className="kpi__k">Fiche client</div>
            <div style={{ fontWeight: 600, marginTop: 6 }}>{selectedContact.name}</div>
            <div className="muted" style={{ fontSize: 13 }}>
              {isForeignClient
                ? ['Client étranger', selectedContact.countryCode, selectedContact.vatId ? 'TVA ' + selectedContact.vatId : null].filter(Boolean).join(' · ')
                : selectedContact.siren
                  ? 'SIREN ' + selectedContact.siren
                  : 'Client français'}
            </div>
          </div>
        ) : contactLoading ? (
          <div className="card--pad" style={{ background: 'var(--surface-sunken)', borderRadius: 'var(--r-md)' }}>
            <span className="muted" style={{ fontSize: 13 }}>
              Chargement de la fiche client…
            </span>
          </div>
        ) : selected ? (
          <div className="card--pad" style={{ background: 'var(--surface-sunken)', borderRadius: 'var(--r-md)' }}>
            <div className="kpi__k">Récupéré via l’annuaire Sirene</div>
            <div style={{ fontWeight: 600, marginTop: 6 }}>{selected.name}</div>
            <div className="muted" style={{ fontSize: 13 }}>
              {[
                [selected.address, selected.postalCode && selected.city ? `${selected.postalCode} ${selected.city}` : selected.city]
                  .filter(Boolean)
                  .join(', ') || null,
                selected.vatNumber ? 'TVA ' + selected.vatNumber : null,
              ]
                .filter(Boolean)
                .join(' · ') || ('SIREN ' + selected.siren)}
            </div>
            {!selected.active ? (
              <div style={{ color: 'var(--danger, #b00020)', fontSize: 12.5, marginTop: 6 }}>
                ⚠ Entreprise déclarée cessée dans l’annuaire : vérifiez avant d’émettre.
              </div>
            ) : null}
          </div>
        ) : null}
        <Field label="Email du client" opt="recommandé" help="Permet l’envoi de la facture si le client n’a pas encore activé sa réception électronique.">
          <input className="ctrl" type="email" placeholder="compta@client.fr" value={email} onChange={(ev) => setEmail(ev.target.value)} />
        </Field>
        <div className="callout-info" style={{ marginTop: 18 }}>
          <Icon name="info" size={19} />
          <p>
            Si votre client n’a pas encore activé sa réception électronique, la facture lui est envoyée par email avec un lien sécurisé.
          </p>
        </div>
      </div>
    ),

    2: (
      <div className="wiz__panel">
        <div className="wiz__qhead">Que facturez-vous ?</div>
        <div className="wiz__qsub">Ajoutez vos prestations ou produits. Les totaux se calculent tout seuls.</div>
        <TemplatePicker onApply={applyTemplate} />
        {isForeignClient ? (
          <div className="callout-info" style={{ marginBottom: 14 }}>
            <Icon name="info" size={19} />
            <p>Client étranger : TVA non applicable sur la facture, la mention légale est ajoutée automatiquement.</p>
          </div>
        ) : null}
        <LineBuilder lines={lines} setLines={setLines} catalog={catalog.rows} vatLocked={isForeignClient} />
        <div className="totals">
          <div className="row">
            <span className="muted">Total HT</span>
            <span className="v">{eur(ht)}</span>
          </div>
          <div className="row">
            <span className="muted">TVA</span>
            <span className="v">{eur(tva)}</span>
          </div>
          <div className="row grand">
            <span>Total TTC</span>
            <span className="v">{eur(ht + tva)}</span>
          </div>
        </div>
      </div>
    ),

    3: (
      <div className="wiz__panel">
        <div className="wiz__qhead">Quand et comment ?</div>
        <div className="wiz__qsub">Choisissez l’échéance. La date d’émission est celle du jour.</div>
        <div className="grid2">
          <Field label="Date d’émission">
            <input className="ctrl" type="text" value={today} readOnly />
          </Field>
          <Field label="Échéance">
            <select className="ctrl" value={dueDays} onChange={(ev) => setDueDays(+ev.target.value)}>
              <option value={30}>À 30 jours</option>
              <option value={45}>À 45 jours</option>
              <option value={0}>À réception</option>
              {/* Délai issu d'un modèle hors des choix standards : option dynamique
                  pour que le <select> reflète la valeur réellement appliquée. */}
              {![0, 30, 45].includes(dueDays) && (
                <option value={dueDays}>À {dueDays} jours</option>
              )}
            </select>
          </Field>
        </div>
        {/* Pas de champ « Conditions particulières » tant que la mention libre n'est
            pas livrée : le textarea existait, avec son placeholder engageant, mais
            disabled en DOM. On clique, rien ne se passe (constat d'audit QW4). */}
      </div>
    ),

    4: (
      <div className="wiz__panel">
        <div className="wiz__qhead">Tout est prêt</div>
        <div className="wiz__qsub">Vérifiez l’aperçu, puis envoyez. Le format et l’archivage légal sont gérés pour vous.</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {(
            [
              ['Client', client || 'Non renseigné'],
              ['Lignes', lines.filter((l) => l.d).length + ' prestation(s)'],
              ['Total TTC', eur(ht + tva)],
              ['Format', 'Factur-X · plateforme agréée'],
              ['Échéance', dueDays === 0 ? 'À réception' : 'À ' + dueDays + ' jours'],
            ] as [string, string][]
          ).map((r, i) => (
            <div
              key={i}
              style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: i < 4 ? '1px solid var(--border-subtle)' : 'none' }}
            >
              <span className="muted">{r[0]}</span>
              <b style={{ fontWeight: 600 }}>{r[1]}</b>
            </div>
          ))}
        </div>
        <div className="callout-info" style={{ marginTop: 18 }}>
          <Icon name="shield" size={19} />
          <p>Format Factur-X et archivage légal gérés pour vous.</p>
        </div>
      </div>
    ),
  };

  return (
    <div>
      <div className="shead">
        <div className="shead__l">
          <button className="btn btn--ghost btn--sm" onClick={() => go('invoices')} style={{ marginBottom: 12 }}>
            <Icon name="arrow-l" size={15} />
            Annuler
          </button>
          <h1>Nouvelle facture</h1>
        </div>
      </div>

      <StepBar step={step} />

      <div className="wiz wiz--preview">
        <div>
          {eq.level === 'warn' && (
            <div className="callout-info" style={{ marginBottom: 16 }}>
              <Icon name="info" size={19} />
              <p>
                Il vous reste {eq.quota - eq.used} facture{eq.quota - eq.used > 1 ? 's' : ''} à émettre ce mois-ci sur votre formule.
              </p>
            </div>
          )}
          {eq.level === 'over' && (
            <div className="callout-info" style={{ marginBottom: 16 }}>
              <Icon name="info" size={18} />
              <div>
                <div className="t">Vous avez dépassé votre forfait ({eq.used}/{eq.quota} ce mois-ci). Vos factures continuent de partir : passez à une formule supérieure pour un quota plus large.</div>
                <Button variant="primary" onClick={() => go('subscription')} style={{ marginTop: 8 }}>
                  Voir les formules
                </Button>
              </div>
            </div>
          )}
          {panels[step]}
          <div className="wiz__foot">
            {step > 1 ? (
              <Button variant="ghost" icon="arrow-l" onClick={() => setStep(step - 1)}>
                Précédent
              </Button>
            ) : (
              <span />
            )}
            {step < 4 ? (
              <Button variant="primary" iconRight="arrow-r" onClick={() => setStep(step + 1)}>
                Continuer
              </Button>
            ) : (
              <Button variant="primary" icon="send" onClick={openConfirm}>
                Émettre la facture
              </Button>
            )}
          </div>
        </div>
        <Preview client={client} lines={lines} issuer={issuer} />
      </div>

      {/* Confirmation de l'émission légale. Le parcours n'en a qu'une : ce même
          dialogue prépare le brouillon et affiche la prévalidation à blanc, puis
          porte la décision d'émettre. Le texte disait auparavant que la facture
          « reste en brouillon », alors que ce bouton attribue le numéro
          séquentiel, consomme un jeton et transmet le document (audit C4). */}
      <ConfirmDialog
        open={confirm}
        title="Émettre cette facture ?"
        cancelLabel="Revenir au brouillon"
        confirmLabel={submitting ? 'Émission…' : 'Émettre la facture'}
        confirmIcon="send"
        busy={submitting}
        // draftMatchesScreen : garde structurelle, le bouton est incapable
        // d'émettre un brouillon qui ne correspond pas à ce que l'écran affiche.
        // La relance de préparation dans openConfirm est le chemin nominal ; cette
        // condition est la ceinture, au cas où un brouillon désaccordé survivrait.
        // contactLoading : le pays d'une fiche locale tout juste sélectionnée n'est
        // pas encore connu (GET /:id en vol) — tant qu'il ne l'est pas, on ignore
        // si la TVA doit être verrouillée à 0, donc l'émission légale attend.
        confirmDisabled={preparing || contactLoading || !draftMatchesScreen || Boolean(validation && validation.errors.length > 0)}
        confirmTitle={
          validation && validation.errors.length > 0
            ? 'Corrigez les erreurs ci-dessus avant d’émettre.'
            : contactLoading
              ? 'Chargement de la fiche client…'
              : preparing
                ? 'Préparation du brouillon en cours…'
                : !draftMatchesScreen
                  ? 'Le brouillon ne correspond pas encore à cette facture.'
                  : undefined
        }
        // Annulation possible pendant la préparation du brouillon : sa création
        // est idempotente (draftPayloadRef), seule l'émission en vol verrouille.
        onClose={() => {
          if (submitting) return;
          setConfirm(false);
        }}
        onConfirm={() => void submit()}
      >
        <div className="recap">
          <div className="recap__item">
            <span className="kpi__k">Client</span>
            <b>{client || 'Non renseigné'}</b>
          </div>
          <div className="recap__item">
            <span className="kpi__k">Total TTC</span>
            <b className="amt">{eur(ht + tva)}</b>
          </div>
        </div>
        <p>
          L’émission est définitive : un numéro de facture séquentiel est attribué,{' '}
          {isForeignClient
            ? 'la facture est transmise à l’administration via e-reporting (client étranger)'
            : 'le document part vers votre client au format Factur-X via la plateforme agréée'}
          , et un jeton de votre forfait est consommé. Une facture émise ne se modifie plus, elle se corrige par un avoir ou une
          facture rectificative.
        </p>

        {preparing ? (
          <p className="muted" style={{ fontSize: 13 }}>Préparation et vérification du brouillon…</p>
        ) : null}

        {validation && validation.errors.length > 0 ? (
          <div className="callout-err" style={{ marginTop: 4, marginBottom: 4 }}>
            <Icon name="alert" size={18} />
            <div>
              <div className="t">Facture non conforme</div>
              <div className="d">
                {validation.errors.map((e, i) => <div key={i}>{e}</div>)}
              </div>
            </div>
          </div>
        ) : null}

        {validation && validation.warnings.length > 0 ? (
          <div className="callout-info" style={{ marginTop: 4, marginBottom: 4 }}>
            <Icon name="info" size={18} />
            <div>
              <div className="t">À vérifier</div>
              <div className="d">
                {validation.warnings.map((w, i) => <div key={i}>{w}</div>)}
              </div>
            </div>
          </div>
        ) : null}

        {error ? (
          <div className="callout-err" style={{ marginTop: 4, marginBottom: 4 }}>
            <Icon name="alert" size={18} />
            <div>
              <div className="t">Envoi impossible</div>
              <div className="d">{error}</div>
              {/* Sans ce bouton, une préparation qui échoue laisse le bouton
                  d'émission désactivé sans issue visible depuis la modale. */}
              {!preparing && !draftMatchesScreen ? (
                <Button variant="ghost" icon="send" onClick={() => void prepareDraft(JSON.stringify(buildPayload()))} style={{ marginTop: 8 }}>
                  Réessayer la préparation
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}
      </ConfirmDialog>
    </div>
  );
}
